/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { BehaviorSubject } from 'rxjs';
import { DevLoggerService } from 'dev-logger-service';
import { LogsApiRequestService } from 'ng/modules/logs/services/logs-api-request.service';
import { LogsEffectsService } from 'ng/modules/logs/services/logs-effects.service';
import { TLogListStateParams, TLogStateParams } from 'ng/modules/logs/logs.types';
import { ILogListStates, LogListStore } from 'ng/modules/logs/services/log-list.store';

import { TTrafficLog, TTrafficLogIdentifier } from '../traffic-logs.types';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { getTrafficLogIdentifier } from '../utils/traffic-logs.utils';

export interface ITrafficLogListStates extends ILogListStates {
    logs: TTrafficLog[];
}

const initialState: ITrafficLogListStates = {
    hasError: false,
    logs: [],
};

/**
 * @abstract
 * @description
 *     An abstract/template component store for traffic log list.
 *     Should be inherited by more concrete traffic log list component stores (eg. PoolLogListStore) to serve a
 *     federated purpose.
 *     The concrete store should override the logListRequestId property to the end component list request ID.
 * @author Zhiqian Liu
 */
export abstract class TrafficLogListStore extends LogListStore<ITrafficLogListStates> {
    // ****************************** Selectors ******************************

    /**
     * The log that's been most recently inspected by the log cinematic view.
     * Set to be a behavior subject since this can always be of use in a c-view opened after its
     * latest value has been emitted.
     */
    public override readonly recentlyViewedLog$ = new BehaviorSubject<TTrafficLog>(null);

    /**
     * The logs that have been highlighted in the table.
     */
    public override readonly selectedLogRows$ = this.select(
        this.logList$,
        this.logsStore.selectedLogs$,
        (logs: TTrafficLog[], selectedLogIds: TTrafficLogIdentifier[]) => {
            const selectedLogIdSet = new Set(selectedLogIds);

            return logs.filter(log => selectedLogIdSet.has(getTrafficLogIdentifier(log)));
        },
    );

    public override readonly getLogList: (apiParams: TLogListStateParams) => void;

    public override readonly refreshList: () => void;

    /**
     * The request ID for the log list request. Should be overridden by the concrete store.
     */
    protected abstract logListRequestId: string;

    public abstract id: number;

    // ********************************** Constructor **********************************

    constructor(
        protected readonly logsStore: TrafficLogsStore<ITrafficLogsStates>,
        protected readonly logsApiRequestService: LogsApiRequestService,
        protected readonly devLoggerService: DevLoggerService,
        protected readonly logsEffectsService: LogsEffectsService,
    ) {
        super(initialState, logsStore, logsApiRequestService, devLoggerService);

        // Initialize request and refresh effects
        const requestLogListEffect = logsEffectsService.createLogsRecursiveRequestEffect(
            this,
            this.startLoading,
            this.stopLoading,
            this.handleApiData,
            this.handleApiError,
            this.handleApiLoopComplete,
        );

        this.getLogList = (apiParams: TLogStateParams): void => {
            requestLogListEffect({
                apiParams,
                config: {
                    requestId: this.logListRequestId,
                },
            });
        };

        this.refreshList = this.effect(
            LogsEffectsService.createRefreshEffect<TLogListStateParams>(
                this.logsStore.logListApiParams$,
                this.getLogList,
            ),
        );
    }

    public override cancelLogListRequest = (): void => {
        this.logsApiRequestService.cancelRequest(this.logListRequestId);
    };
}
